import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import { Input } from '../Input/Input.js';
import { Select } from '../Select/Select.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import { parseColor, validateAndClampColor, formatOklch, generateP3CSS, formatP3Hex, colorsEqual, getContrastColor, getMaxChromaForWorkspace, getRelativeChroma } from '../../utils/colorUtils.js';
import { 
  getAvailableReferenceColors, 
  getReferenceColorPlaceholders, 
  calculateLinkedColor,
  calculateContrastColor
} from '../../utils/linkedColorUtils.js';
import { measureContrastPair, roundContrastLevel, colorInputForMeasurement } from '../../utils/wcagContrast.js';


import { LCHAInput } from '../LCHAInput/LCHAInput.js';
import { MoveModal } from '../MoveModal/MoveModal.js';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu.js';
import { useEditState } from '../../hooks/useEditState.js';
import './CardColor.css';

export const CardColor = ({
  mode = 'static', // 'static', 'linked', 'contrast'
  colorName = 'Color Name',
  colorValue = '#34B9FF',
  oklchValue = 'oklch(0.5 0.25 240)',
  p3Support = true,
  isToken = false, // Whether this is in themes section (filters mode options)
  isEditing = false, // Whether this color should start in editing mode
  // Folder moving support
  folderOptions = [],
  currentFolderId = null,
  
  // Linked color props
  referenceColorId = '',
  lExpression = '',
  cExpression = '',
  hExpression = '',
  aExpression = '',
  
  // Contrast-based color props
  backgroundColorId = '',
  foregroundColorId = '',
  contrastLevel = 60,
  useAlpha = false,
  /** 'apca' | 'wcag' — must match global app setting for contrast-based colors */
  contrastAlgorithm = 'apca',
  /** 'srgb' | 'p3' — workspace gamut: parsing, relative chroma %, static sliders, and APCA measurement. */
  colorSpace = 'p3',
  
  // Available colors for reference selection (when excludeColorId is set, pass full list and we filter here for stable refs)
  availableColors = [],
  excludeColorId = null,
  // Section titles for color dropdown (collection names)
  primitivesSectionTitle = 'Primitives',
  themesSectionTitle = 'Themes',
  // Precomputed color map: Map<colorId, { isValid, hex, oklch }>
  precomputedColors = null,
  currentColorId = '',
  
  // Callbacks
  onModeChange,
  onColorNameChange,
  onColorValueChange,
  onEditingComplete, // Called when editing is finished
  onReferenceColorChange,
  onLCHAExpressionChange,
  onBackgroundColorChange,
  onForegroundColorChange,
  onContrastLevelChange,
  onContrastAlphaChange,
  onMoreAction,
  className = '',
  variant = 'default',
  ...props
}) => {
  // One stable list from parent; filter to exclude this card when excludeColorId is provided (perf: avoids N lists in Main).
  const effectiveAvailableColors = useMemo(
    () =>
      excludeColorId != null
        ? (availableColors || []).filter((c) => (c.id || c.value) !== excludeColorId)
        : availableColors || [],
    [availableColors, excludeColorId]
  );

  // Manual state management for color value editing to avoid sync issues

  const [isEditingColorValue, setIsEditingColorValue] = useState(false);
  const [localColorValue, setLocalColorValue] = useState(colorValue);
  const [showStaticLchSliders, setShowStaticLchSliders] = useState(false);
  const lastCommittedColorValueRef = useRef(colorValue);
  const isExplicitlyCancelingRef = useRef(false);
  const staticColorPropagationTimeoutRef = useRef(null);
  const STATIC_COLOR_PROPAGATION_DELAY_MS = 1000;

  // Update local value when prop changes (but not during editing)
  useEffect(() => {
    if (!isEditingColorValue && colorValue !== lastCommittedColorValueRef.current) {
      setLocalColorValue(colorValue);
      lastCommittedColorValueRef.current = colorValue;
    }
  }, [colorValue, isEditingColorValue]);
  
  const startColorValueEditing = () => {
    setIsEditingColorValue(true);
  };
  
  const updateColorValue = (newValue) => {
    setLocalColorValue(newValue);
  };
  
  const scheduleStaticColorPropagation = (value) => {
    if (staticColorPropagationTimeoutRef.current) {
      clearTimeout(staticColorPropagationTimeoutRef.current);
    }
    staticColorPropagationTimeoutRef.current = setTimeout(() => {
      staticColorPropagationTimeoutRef.current = null;
      if (onColorValueChange && value !== lastCommittedColorValueRef.current) {
        onColorValueChange(value);
        lastCommittedColorValueRef.current = value;
      }
    }, STATIC_COLOR_PROPAGATION_DELAY_MS);
  };

  const commitColorValue = (value = localColorValue) => {
    setIsEditingColorValue(false);
    if (onColorValueChange && value !== lastCommittedColorValueRef.current) {
      onColorValueChange(value);
      lastCommittedColorValueRef.current = value;
    }
    isExplicitlyCancelingRef.current = false;
  };
  
  const cancelColorValueEditing = () => {
    isExplicitlyCancelingRef.current = true;
    setIsEditingColorValue(false);
    setLocalColorValue(lastCommittedColorValueRef.current);
  };
  
  // Manual state management for color name editing to avoid sync issues
  const [isEditingName, setIsEditingName] = useState(isEditing);
  const [localColorName, setLocalColorName] = useState(colorName);
  const lastCommittedColorNameRef = useRef(colorName);
  const isExplicitlyCancelingNameRef = useRef(false);
  const committedOnEnterRef = useRef(false);

  // Update local value when prop changes (but not during editing)
  useEffect(() => {
    if (!isEditingName && colorName !== lastCommittedColorNameRef.current) {
      setLocalColorName(colorName);
      lastCommittedColorNameRef.current = colorName;
    }
  }, [colorName, isEditingName]);

  const startNameEditing = () => {
    setIsEditingName(true);
  };
  
  const updateNameValue = (newValue) => {
    setLocalColorName(newValue);
  };
  
  const commitNameValue = (value = localColorName) => {
    setIsEditingName(false);
    const trimmed = (value || '').trim();
    const isEmpty = trimmed === '';
    // Always notify parent when empty so it can normalize to "Color" / "Color 2"
    if (onColorNameChange && (value !== lastCommittedColorNameRef.current || isEmpty)) {
      onColorNameChange(isEmpty ? '' : value);
      // When empty, immediately sync to current prop so field shows the name
      if (isEmpty) {
        setLocalColorName(colorName);
        lastCommittedColorNameRef.current = colorName;
      } else {
        lastCommittedColorNameRef.current = value;
      }
    }
    if (onEditingComplete) {
      onEditingComplete();
    }
    isExplicitlyCancelingNameRef.current = false;
  };
  
  const cancelNameEditing = () => {
    isExplicitlyCancelingNameRef.current = true;
    setIsEditingName(false);
    setLocalColorName(lastCommittedColorNameRef.current);
  };
  
  const cleanupNameEditing = () => {
    // No cleanup needed for manual state management
  };
  const [parsedColor, setParsedColor] = useState(null);
  const nameInputRef = useRef(null);

  // Focus and select the color name when entering editing mode, regardless of how it was activated
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Build lightweight options from availableColors using precomputed results (no recalculation)
  const mapAvailableToOptions = (colors) => {
    const result = (colors || []).map(c => {
      let optionColor = '#808080'; // Default fallback
      
      
      // SINGLE SOURCE OF TRUTH: Always use precomputed colors first
      // Use c.value (the ID) to look up precomputed colors
      const colorId = c.value || c.id;
      if (precomputedColors && precomputedColors.get && colorId) {
        const pre = precomputedColors.get(colorId);
        if (pre) {
          if (pre.hex) {
            optionColor = pre.hex;
          } else if (pre.oklch) {
            // Convert OKLCH to hex if available
            const { l, c: chroma, h, alpha = 1 } = pre.oklch;
            if (alpha < 1) {
              optionColor = `oklch(${l.toFixed(3)} ${chroma.toFixed(3)} ${Math.round(h || 0)} / ${alpha.toFixed(3)})`;
            } else {
              optionColor = `oklch(${l.toFixed(3)} ${chroma.toFixed(3)} ${Math.round(h || 0)})`;
            }
          }
          // Attach a unified CSS color string (P3-aware) so consumers don't recalc
          if (pre.isValid) {
            try {
              // generateP3CSS accepts a parsed color object
              // Use pre directly since engine returns parsed results
              // Fallback handled inside generateP3CSS
              // @ts-ignore runtime JS
              var cssColor = generateP3CSS(pre, colorSpace);
              // Store alongside option for Select
              c.displayCss = cssColor;
            } catch (e) {}
          }
        }
      }
      
      // If no precomputed color, use the color that getAvailableReferenceColors already provided
      if (optionColor === '#808080' && c.color && c.color !== '#808080') {
        optionColor = c.color;
      }
      
      return {
        value: c.value || c.id,
        label: c.label || c.name || 'Color',
        color: optionColor,
        displayCss: c.displayCss,
        folderId: c.folderId,
        folderName: c.folderName,
        folderPath: c.folderPath,
        section: c.section,
        isToken: c.isToken,
      };
    });
    return result;
  };
  
  // Linked color state
  const [localReferenceColorId, setLocalReferenceColorId] = useState(referenceColorId);
  const [localLExpression, setLocalLExpression] = useState(lExpression);
  const [localCExpression, setLocalCExpression] = useState(cExpression);
  const [localHExpression, setLocalHExpression] = useState(hExpression);
  const [localAExpression, setLocalAExpression] = useState(aExpression);
  
  // Contrast-based color state
  const [localBackgroundColorId, setLocalBackgroundColorId] = useState(backgroundColorId);
  const [localForegroundColorId, setLocalForegroundColorId] = useState(foregroundColorId);
  const [localContrastLevel, setLocalContrastLevel] = useState(contrastLevel);
  const lastCommittedContrastLevelRef = useRef(contrastLevel);
  const isExplicitlyCancelingContrastRef = useRef(false);
  const [localContrastAlpha, setLocalContrastAlpha] = useState(useAlpha);
  const [backgroundColorRef, setBackgroundColorRef] = useState('Color Name');
  const [foregroundColorRef, setForegroundColorRef] = useState('Color Name');
  const [lcValue, setLcValue] = useState('108');
  const [actualContrast, setActualContrast] = useState(null);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showMoveModal, setShowMoveModal] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  // OKLCH component values for linked mode
  const [lValue, setLValue] = useState('0.5');
  const [cValue, setCValue] = useState('0.25');
  const [hValue, setHValue] = useState('240');
  const [aValue, setAValue] = useState('100');

  const baseClass = 'card-color';
  const modeClass = `card-color--${mode}`;
  
  // Extract border-related classes for the inner content
  const borderClasses = [
    'card-color__content--first-unfoldered',
    'card-color__content--first-in-folder',
    'card-color__content--last-in-folder'
  ];
  
  const contentBorderClasses = borderClasses.filter(cls => className.includes(cls));
  const otherClasses = className.split(' ').filter(cls => !borderClasses.includes(cls) && cls !== 'card-color__content--last-unfoldered');
  
  // Special handling for last-unfoldered and last-in-folder: apply only to outer card
  const hasLastUnfoldered = className.includes('card-color__content--last-unfoldered');
  const hasLastInFolder = className.includes('card-color__content--last-in-folder');
  const isDemo = variant === 'demo';
  const outerClasses = [baseClass, modeClass, ...otherClasses];
  if (hasLastUnfoldered) outerClasses.push('card-color--last-unfoldered');
  if (hasLastInFolder) outerClasses.push('card-color--last-in-folder');
  if (isDemo) outerClasses.push('card-color--variant-demo');
  
  const combinedClassName = outerClasses.filter(Boolean).join(' ');
  const contentClassName = ['card-color__content', ...contentBorderClasses].filter(Boolean).join(' ');

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreDropdown && 
          dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
    };

    const handleScroll = () => {
      if (showMoreDropdown) {
        setShowMoreDropdown(false);
      }
    };

    const handleResize = () => {
      if (showMoreDropdown) {
        setShowMoreDropdown(false);
      }
    };

    if (showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside);

      // Defer scroll/resize listeners by one frame to avoid catching
      // layout-triggered scroll events during the initial render
      const rafId = requestAnimationFrame(() => {
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
      });

      return () => {
        cancelAnimationFrame(rafId);
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreDropdown]);

  // Track if any LCHA input is currently being edited
  const [isEditingLCHA, setIsEditingLCHA] = useState(false);

  // Focus and select the color name when entering editing mode
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Cleanup on unmount - no cleanup needed for manual state management
  useEffect(() => {
    return () => {
      // No cleanup needed
    };
  }, []);

  // Use precomputed result when provided, otherwise fallback to local calculation
  useEffect(() => {
    // While typing or using static LCH sliders, prefer live local parsing for immediate swatch preview
    const shouldUsePrecomputed = !(mode === 'static' && (isEditingColorValue || showStaticLchSliders));
    if (shouldUsePrecomputed && precomputedColors && currentColorId && precomputedColors.get) {
      const pre = precomputedColors.get(currentColorId);
      if (pre) {
        setParsedColor(pre);
        return;
      }
    }
    // Skip calculations during LCHA editing, but allow static color calculations
    if (isEditingLCHA) return;
    
    // SINGLE SOURCE OF TRUTH: Use precomputed colors for all modes
    let calculatedColor = null;
    if (mode === 'static') {
      // For static colors, parse local input live while typing
      calculatedColor = parseColor(localColorValue, colorSpace);
    } else {
      // For linked/contrast colors, use precomputed result only
      // No fallback calculations - precomputed colors should contain everything
      calculatedColor = null; // Will be set by precomputed colors at the top of this useEffect
    }
    
    // Only set if we have a calculated result (static colors)
    if (calculatedColor) {
      setParsedColor(calculatedColor);
    }
  }, [precomputedColors, currentColorId, mode, localColorValue, isEditingColorValue, showStaticLchSliders, localReferenceColorId, localLExpression, localCExpression, localHExpression, localAExpression, localBackgroundColorId, localForegroundColorId, localContrastLevel, localContrastAlpha, isEditingLCHA, colorSpace]);

  // Calculate actual contrast for contrast-based colors
  useEffect(() => {
    // Skip contrast calculation if user is actively editing
    if (isEditingLCHA || isEditingColorValue) {
      return;
    }
    
    if (mode === 'contrast' && parsedColor && parsedColor.isValid && localBackgroundColorId) {
      try {
        // Use precomputed colors instead of recalculating
        if (precomputedColors && precomputedColors.get) {
          const bgColorData = precomputedColors.get(localBackgroundColorId);
          if (bgColorData && bgColorData.isValid && bgColorData.hex) {
            // In P3 mode, measure on the full-precision OKLCH (preserves P3-only
            // chroma) so the displayed Lc matches what tools like Polychrom read
            // off the same OKLCH color in a P3 document. The hex roundtrip would
            // sRGB-clamp the color and shift Lc by ~1–2 for saturated inputs.
            const fgInput = colorInputForMeasurement(parsedColor, colorSpace);
            const bgInput = colorInputForMeasurement(bgColorData, colorSpace);
            const contrast = measureContrastPair(fgInput, bgInput, contrastAlgorithm, colorSpace);
            setActualContrast(contrast);
          } else {
            setActualContrast(null);
          }
        } else if (effectiveAvailableColors.length > 0) {
          // Fallback to direct calculation only if precomputed colors not available
          const bgColor = effectiveAvailableColors.find(color => color.id === localBackgroundColorId);
          if (bgColor) {
            let bgColorData;
            if (bgColor.mode === 'static') {
              bgColorData = parseColor(bgColor.colorValue || bgColor.hexValue || '#808080', colorSpace);
            } else if (bgColor.mode === 'linked') {
              bgColorData = calculateLinkedColor(
                effectiveAvailableColors,
                bgColor.referenceColorId,
                bgColor.lExpression,
                bgColor.cExpression,
                bgColor.hExpression,
                bgColor.aExpression,
                0,
                contrastAlgorithm,
                colorSpace
              );
            } else if (bgColor.mode === 'contrast') {
              bgColorData = calculateContrastColor(
                effectiveAvailableColors.filter(c => c.id !== bgColor.id),
                bgColor.backgroundColorId,
                bgColor.foregroundColorId,
                bgColor.contrastLevel || 60,
                bgColor.useAlpha || false,
                contrastAlgorithm,
                colorSpace
              );
            }
            
            if (bgColorData && bgColorData.isValid) {
              const fgInput = colorInputForMeasurement(parsedColor, colorSpace);
              const bgInput = colorInputForMeasurement(bgColorData, colorSpace);
              const contrast = measureContrastPair(fgInput, bgInput, contrastAlgorithm, colorSpace);
              setActualContrast(contrast);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to calculate actual contrast:', error);
        setActualContrast(null);
      }
    } else {
      setActualContrast(null);
    }
  }, [mode, parsedColor, localBackgroundColorId, precomputedColors, isEditingLCHA, isEditingColorValue, contrastAlgorithm, colorSpace, effectiveAvailableColors]);

  // Clear static color propagation timeout on unmount
  useEffect(() => {
    return () => {
      if (staticColorPropagationTimeoutRef.current) {
        clearTimeout(staticColorPropagationTimeoutRef.current);
      }
    };
  }, []);

  // Sync props to local state
  useEffect(() => {
    // Note: localColorValue is now managed by useEditState hook
    setLocalReferenceColorId(referenceColorId);
    setLocalLExpression(lExpression);
    setLocalCExpression(cExpression);
    setLocalHExpression(hExpression);
    setLocalAExpression(aExpression);
    setLocalBackgroundColorId(backgroundColorId);
    setLocalForegroundColorId(foregroundColorId);
    const roundedLevel =
      contrastLevel != null && contrastLevel !== ''
        ? roundContrastLevel(Number(contrastLevel), contrastAlgorithm)
        : contrastLevel;
    setLocalContrastLevel(roundedLevel);
    lastCommittedContrastLevelRef.current = roundedLevel;
    setLocalContrastAlpha(useAlpha);
  }, [colorValue, referenceColorId, lExpression, cExpression, hExpression, aExpression, backgroundColorId, foregroundColorId, contrastLevel, useAlpha, contrastAlgorithm]);

  // Auto-select first available color when switching to linked mode
  useEffect(() => {
    if (mode === 'linked' && !localReferenceColorId && effectiveAvailableColors.length > 0) {
      const referenceOptions = getAvailableReferenceColors(effectiveAvailableColors, currentColorId, isToken, colorSpace);
      if (referenceOptions.length > 0) {
        const firstOption = referenceOptions[0];
        setLocalReferenceColorId(firstOption.value);
        if (onReferenceColorChange) {
          onReferenceColorChange(firstOption.value);
        }
      }
    }
  }, [mode, localReferenceColorId, currentColorId, isToken, onReferenceColorChange, effectiveAvailableColors, colorSpace]);

  // Auto-select first available colors when switching to contrast mode
  useEffect(() => {
    if (mode === 'contrast' && effectiveAvailableColors.length > 0) {
      const referenceOptions = getAvailableReferenceColors(effectiveAvailableColors, currentColorId, isToken, colorSpace);
      if (referenceOptions.length > 0) {
        const firstOption = referenceOptions[0];
        // Try to use different colors for background and foreground when possible
        const secondOption = referenceOptions.length > 1 ? referenceOptions[1] : firstOption;
        
        if (!localBackgroundColorId) {
          setLocalBackgroundColorId(firstOption.value);
          if (onBackgroundColorChange) {
            onBackgroundColorChange(firstOption.value);
          }
        }
        
        if (!localForegroundColorId) {
          setLocalForegroundColorId(secondOption.value);
          if (onForegroundColorChange) {
            onForegroundColorChange(secondOption.value);
          }
        }
      }
    }
  }, [mode, localBackgroundColorId, localForegroundColorId, currentColorId, isToken, onBackgroundColorChange, onForegroundColorChange, effectiveAvailableColors, colorSpace]);

  // Mode options filtered based on isToken prop
  const allModeOptions = [
    { value: 'static', label: 'Static' },
    { value: 'linked', label: 'Linked' },
    { value: 'contrast', label: 'Contrast-based' }
  ];
  
  const modeOptions = isToken 
    ? allModeOptions.filter(option => option.value !== 'static') // Themes: only linked and contrast
    : allModeOptions; // Primitives: all modes

  const colorReferenceOptions = [
    { value: 'color1', label: 'Color Name', color: '#006aaf' },
    { value: 'color2', label: 'Primary Blue', color: '#0066cc' },
    { value: 'color3', label: 'Accent Color', color: '#ff6b35' }
  ];

  const handleModeChange = (option) => {
    if (onModeChange) {
      onModeChange(option.value);
    }
  };

  const handleMoreAction = (e) => {
    if (!showMoreDropdown) {
      // Calculate dropdown position when opening
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const dropdownTop = buttonRect.bottom + 4; // 4px gap below button
      const dropdownLeft = buttonRect.right - 120; // Align right edge (120px is min-width)
      
      setDropdownPosition({
        top: dropdownTop,
        left: Math.max(8, dropdownLeft) // Ensure at least 8px from left edge
      });
    }
    setShowMoreDropdown(!showMoreDropdown);
  };

  const handleMoreOptionClick = (action) => {
    setShowMoreDropdown(false);
    if (action === 'rename') {
      startNameEditing();
    } else if (action === 'move') {
      setShowMoveModal(true);
    } else if (onMoreAction) {
      onMoreAction(action);
    }
  };

  const handleMoveModalClose = () => {
    setShowMoveModal(false);
  };

  const handleMoveModalMove = (targetFolderId) => {
    if (onMoreAction) {
      onMoreAction('move', targetFolderId);
    }
  };

  const handleNameSubmit = (value) => {
    commitNameValue(value);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      committedOnEnterRef.current = true;
      handleNameSubmit(e.target.value);
      e.target.blur();
    } else if (e.key === 'Escape') {
      cancelNameEditing();
      e.target.blur();
    }
  };

  const handleNameBlur = (e) => {
    if (isExplicitlyCancelingNameRef.current) {
      isExplicitlyCancelingNameRef.current = false;
      return;
    }
    if (committedOnEnterRef.current) {
      committedOnEnterRef.current = false;
      return;
    }
    const valueAtBlur = e && e.target ? e.target.value : localColorName;
    commitNameValue(valueAtBlur);
  };

  const handleColorValueChange = (e) => {
    const newValue = e.target.value;
    
    // Start editing on first change
    if (!isEditingColorValue) {
      startColorValueEditing();
    }
    
    // Update local value only
    updateColorValue(newValue);
  };

  const handleColorValueKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      // Validate and commit the color
      const validatedColor = validateAndClampColor(localColorValue, colorSpace);
      if (validatedColor && validatedColor.isValid) {
        commitColorValue(localColorValue);
      } else {
        // Invalid color - still commit what user typed
        commitColorValue(localColorValue);
      }
      e.target.blur(); // Remove focus from input
    }
  };

  const handleColorValueBlur = () => {
    // Don't process blur if we're explicitly canceling
    if (isExplicitlyCancelingRef.current) {
      isExplicitlyCancelingRef.current = false;
      return;
    }
    
    // Validate color but keep the original format if valid
    const validatedColor = validateAndClampColor(localColorValue, colorSpace);
    if (validatedColor && validatedColor.isValid) {
      // Check if the parsed color is different from the input (clamping occurred)
      const originalInput = validatedColor.originalInput;
      const clampedOklch = formatOklch(validatedColor.oklch);
      
      // If this was an OKLCH input and clamping occurred, show the clamped value
      if (originalInput.toLowerCase().includes('oklch') && !colorsEqual(originalInput, clampedOklch, colorSpace)) {
        // Update the local value through the edit state hook
        updateColorValue(clampedOklch);
      }
      
      // Commit the validated color
      commitColorValue(localColorValue);
    } else {
      // Invalid color - cancel editing
      cancelColorValueEditing();
    }
  };

  // Debounced handlers for LCHA inputs - only trigger on blur
  const handleLCHAChange = (component, value) => {
    // Mark as editing to prevent heavy calculations
    setIsEditingLCHA(true);
    
    // Only update local state, don't trigger calculation
    switch (component) {
      case 'l':
        setLocalLExpression(value);
        break;
      case 'c':
        setLocalCExpression(value);
        break;
      case 'h':
        setLocalHExpression(value);
        break;
      case 'a':
        setLocalAExpression(value);
        break;
    }
    // Don't call onLCHAExpressionChange here - wait for blur
  };

  const handleLCHABlur = (component) => {
    // Mark as no longer editing to resume calculations
    setIsEditingLCHA(false);
    
    // Trigger calculation only when user finishes editing
    if (onLCHAExpressionChange) {
      let value;
      switch (component) {
        case 'l':
          value = localLExpression;
          break;
        case 'c':
          value = localCExpression;
          break;
        case 'h':
          value = localHExpression;
          break;
        case 'a':
          value = localAExpression;
          break;
        default:
          return;
      }
      onLCHAExpressionChange(component, value);
    }
  };

  // Debounced handlers for contrast inputs
  const handleContrastLevelChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty string for better UX
    if (inputValue === '') {
      setLocalContrastLevel('');
      return; // Don't trigger calculation here
    }
    
    const value = parseFloat(inputValue);
    
    // Check if it's a valid number
    if (isNaN(value)) {
      return; // Don't update if invalid
    }
    
    if (contrastAlgorithm === 'wcag') {
      const clampedValue = Math.max(1, Math.min(21, value));
      setLocalContrastLevel(clampedValue);
    } else {
      const clampedValue = Math.max(0, Math.min(108, value));
      setLocalContrastLevel(clampedValue);
    }
    // Don't call onContrastLevelChange here - wait for blur
  };

  const commitContrastLevel = () => {
    if (!onContrastLevelChange) return;
    const fallback = contrastAlgorithm === 'wcag' ? 4.5 : 1;
    const raw = localContrastLevel === '' ? fallback : Number(localContrastLevel);
    const safe = isFinite(raw) ? raw : fallback;
    const value = roundContrastLevel(safe, contrastAlgorithm);
    setLocalContrastLevel(value);
    onContrastLevelChange(value);
    lastCommittedContrastLevelRef.current = value;
  };

  const handleContrastLevelKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      commitContrastLevel();
      e.target.blur(); // Remove focus from input
    }
  };

  const handleContrastLevelBlur = () => {
    // Don't process blur if we're explicitly canceling
    if (isExplicitlyCancelingContrastRef.current) {
      isExplicitlyCancelingContrastRef.current = false;
      return;
    }

    commitContrastLevel();
  };

  // Render dropdown as a portal to avoid clipping issues
  const renderDropdown = () => {
    if (!showMoreDropdown) return null;

    return createPortal(
      <DropdownMenu
        menuRef={dropdownRef}
        fixed
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`
        }}
      >
        <DropdownMenu.Item onClick={() => handleMoreOptionClick('rename')}>
          Rename
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={() => handleMoreOptionClick('duplicate')}>
          Duplicate
        </DropdownMenu.Item>
        <DropdownMenu.Divider />
        <DropdownMenu.Item onClick={() => handleMoreOptionClick('move')}>
          Move to...
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={() => handleMoreOptionClick('move-up')}>
          Move up
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={() => handleMoreOptionClick('move-down')}>
          Move down
        </DropdownMenu.Item>
        <DropdownMenu.Divider />
        <DropdownMenu.Item danger onClick={() => handleMoreOptionClick('delete')}>
          Delete
        </DropdownMenu.Item>
      </DropdownMenu>,
      document.body
    );
  };

  const renderTypeAndName = () => {
    const getModeLabel = () => {
      switch (mode) {
        case 'static': return 'Static';
        case 'linked': return 'Linked';
        case 'contrast': return 'Contrast-based';
        default: return 'Static';
      }
    };

    return (
      <div className="card-color__type-and-name">
        {isDemo ? (
          <span className="card-color__mode-label">{getModeLabel()}</span>
        ) : (
          <Select
            type="default"
            style="ghost"
            value={mode}
            options={modeOptions}
            onChange={handleModeChange}
            className="card-color__mode-select"
            showSectionHeaders={false}
            placeholder={isToken && mode === 'static' ? 'Select color mode' : undefined}
            warning={isToken && mode === 'static'}
          />
        )}
        {isDemo ? (
          <span className="card-color__name-text">{colorName}</span>
        ) : (
          <Input
            ref={nameInputRef}
            type="text"
            style="ghost"
            value={localColorName}
            onChange={(e) => updateNameValue(e.target.value)}
            onKeyDown={handleNameKeyDown}
            onBlur={handleNameBlur}
            onFocus={(e) => {
              // Enter editing mode whenever the field is focused (Add, Rename, or direct click)
              if (!isEditingName) {
                startNameEditing();
              }
              // Small delay to ensure focus is established before selecting
              setTimeout(() => e.target.select(), 0);
            }}
            placeholder="Color Name"
            className={`card-color__name-input ${isEditingName ? 'card-color__name-input--editing' : ''}`}
            autoFocus={isEditingName}
          />
        )}
      </div>
    );
  };

  const renderStaticParameters = () => {
    // Derive LCH from current color for sliders (static mode uses localColorValue → parsedColor)
    const oklch = (parsedColor && parsedColor.oklch)
      ? parsedColor.oklch
      : { l: 0.5, c: 0.12, h: 240, alpha: 1 };
    const l = Math.max(0, Math.min(1, oklch.l));
    const c = Math.max(0, oklch.c);
    const h = (oklch.h != null && !isNaN(oklch.h)) ? ((oklch.h % 360) + 360) % 360 : 240;
    const maxChromaForLH = getMaxChromaForWorkspace(l, h, oklch.alpha ?? 1, colorSpace);
    const maxC = Math.min(0.4, Math.max(0.001, maxChromaForLH));
    const clampedC = Math.max(0, Math.min(maxC, c));

    // Detect if color value uses relative chroma (oklch(l X% h))
    const isChromaRelative = (() => {
      const match = localColorValue.match(/oklch\s*\(\s*([^)]+)\s*\)/i);
      if (!match) return false;
      const parts = match[1].split(/\s+|\//).map((p) => p.trim());
      const cPart = parts[1];
      return typeof cPart === 'string' && cPart.endsWith('%');
    })();

    // Current relative chroma (0–1) for preserving when L or H change
    const relChroma01 = parsedColor?.oklch ? getRelativeChroma(parsedColor.oklch, colorSpace) : 0;

    const toggleChromaRelativeAbsolute = () => {
      if (!parsedColor || !parsedColor.oklch) return;
      const alpha = parsedColor.oklch.alpha ?? 1;
      const L = Math.round(parsedColor.oklch.l * 1000) / 1000;
      const H = Math.round(parsedColor.oklch.h || 0);
      if (isChromaRelative) {
        const str = formatOklch(parsedColor.oklch);
        setLocalColorValue(str);
        if (onColorValueChange) onColorValueChange(str);
      } else {
        const rel = getRelativeChroma(parsedColor.oklch, colorSpace);
        const pct = Math.round(rel * 100);
        const str = alpha < 1
          ? `oklch(${L} ${pct}% ${H} / ${alpha})`
          : `oklch(${L} ${pct}% ${H})`;
        setLocalColorValue(str);
        if (onColorValueChange) onColorValueChange(str);
      }
    };

    const applyStaticLch = (newL, newC, newH) => {
      const alpha = oklch.alpha ?? 1;
      if (isChromaRelative) {
        const L = Math.round(newL * 1000) / 1000;
        const H = Math.round(newH);
        const pct = (newL !== l || newH !== h)
          ? Math.round(relChroma01 * 100)
          : (() => {
              const maxCNew = getMaxChromaForWorkspace(newL, newH, alpha, colorSpace);
              // Clamp absolute chroma to maxChroma before calculating relative percentage
              const clampedNewC = Math.min(maxCNew, Math.min(0.4, newC));
              const newRel = maxCNew > 0 ? clampedNewC / maxCNew : 0;
              return Math.round(newRel * 100);
            })();
        const str = alpha < 1
          ? `oklch(${L} ${pct}% ${H} / ${alpha})`
          : `oklch(${L} ${pct}% ${H})`;
        setLocalColorValue(str);
        if (onColorValueChange) scheduleStaticColorPropagation(str);
      } else {
        const newMaxC = getMaxChromaForWorkspace(newL, newH, alpha, colorSpace);
        const cappedC = Math.min(0.4, Math.min(newMaxC, newC));
        const str = formatOklch({ mode: 'oklch', l: newL, c: cappedC, h: newH, alpha });
        setLocalColorValue(str);
        if (onColorValueChange) scheduleStaticColorPropagation(str);
      }
    };

    const lGradient = `linear-gradient(to right, oklch(0.01 0 ${h}), oklch(0.5 ${clampedC} ${h}) 50%, oklch(0.99 0 ${h}))`;
    // Chroma: full track width = 0.4; gradient occupies (maxC/0.4)*100%; rest is foreground-15
    const cGradientEndPercent = (maxC / 0.4) * 100;
    const cGradient = `linear-gradient(to right, oklch(${l} 0 ${h}), oklch(${l} ${maxC} ${h}) ${cGradientEndPercent}%, var(--color-foreground-15) ${cGradientEndPercent}%)`;
    const hStops = [0, 60, 120, 180, 240, 300, 360]
      .map((deg) => `oklch(${l} ${clampedC} ${deg})`)
      .join(', ');
    const hGradient = `linear-gradient(to right, ${hStops})`;

    return (
      <div className={`card-color__parameters card-color__parameters--static ${showStaticLchSliders ? 'card-color__parameters--sliders-open' : ''}`}>
        <div className="card-color__static-color-row">
          <Input
            type="text"
            style="outline"
            value={localColorValue}
            onChange={handleColorValueChange}
            onKeyDown={handleColorValueKeyDown}
            onBlur={handleColorValueBlur}
            placeholder="oklch, hsl, rgb, other..."
            label="Color value"
            className={`card-color__color-value-input ${
              parsedColor && !parsedColor.isValid ? 'card-color__input--invalid' : ''
            }`}
          />
          <Button
            type={showStaticLchSliders ? 'solid' : 'outline'}
            size="medium"
            icon={<Icon name="color-sliders" />}
            onClick={() => setShowStaticLchSliders((prev) => !prev)}
            className="card-color__sliders-toggle tooltip"
            data-tooltip="LCH sliders"
            data-tooltip-position="top"
            aria-label={showStaticLchSliders ? 'Hide color sliders' : 'Show color sliders'}
          />
        </div>
        {showStaticLchSliders && (
          <div
            className="card-color__lch-sliders"
            style={{ '--lch-thumb-color': (parsedColor && parsedColor.hex) ? parsedColor.hex : '#808080' }}
          >
            <div className="card-color__lch-slider" data-slider="l">
              <label className="card-color__lch-slider-label">L</label>
              <div className="card-color__lch-slider-track" style={{ background: lGradient }}>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={l}
                  onChange={(e) => {
                    const newL = parseFloat(e.target.value);
                    applyStaticLch(newL, clampedC, h);
                  }}
                  className="card-color__lch-slider-input"
                />
              </div>
            </div>
            <div className="card-color__lch-slider card-color__lch-slider--chroma" data-slider="c">
              <label className="card-color__lch-slider-label">C</label>
              <div 
                className="card-color__lch-slider-track" 
                style={{ 
                  background: cGradient,
                  '--chroma-valid-range': isChromaRelative ? `${(maxC / 0.4) * 100}%` : '100%'
                }}
              >
                <input
                  type="range"
                  min={0}
                  max={isChromaRelative ? (maxC > 0 ? 100 / (maxC / 0.4) : 100) : 0.4}
                  step={isChromaRelative ? 1 : 0.01}
                  value={isChromaRelative ? Math.round(relChroma01 * 100) : clampedC}
                  onChange={(e) => {
                    if (isChromaRelative) {
                      const relPct = parseFloat(e.target.value);
                      const rel01 = Math.max(0, Math.min(1, relPct / 100));
                      const newMaxC = getMaxChromaForWorkspace(l, h, oklch.alpha ?? 1, colorSpace);
                      // Ensure absolute chroma never exceeds maxChroma for current L and H
                      const absoluteC = Math.min(newMaxC, newMaxC * rel01);
                      // Clamp to 0.4 absolute maximum
                      const clampedAbsoluteC = Math.min(0.4, absoluteC);
                      applyStaticLch(l, clampedAbsoluteC, h);
                    } else {
                      const rawC = parseFloat(e.target.value);
                      const newMaxC = getMaxChromaForWorkspace(l, h, oklch.alpha ?? 1, colorSpace);
                      const cappedC = Math.min(0.4, Math.min(newMaxC, rawC));
                      applyStaticLch(l, cappedC, h);
                    }
                  }}
                  className="card-color__lch-slider-input"
                />
              </div>
              <Button
                type={isChromaRelative ? 'solid' : 'outline'}
                size="medium"
                icon={<Icon name={isChromaRelative ? 'lock' : 'lock-open'} />}
                onClick={toggleChromaRelativeAbsolute}
                className="card-color__chroma-lock tooltip"
                data-tooltip={isChromaRelative ? 'Unlock Relative Chroma' : 'Lock Relative Chroma'}
                data-tooltip-position="top"
                aria-label={isChromaRelative ? 'Use absolute chroma' : 'Use relative chroma'}
              />
            </div>
            <div className="card-color__lch-slider" data-slider="h">
              <label className="card-color__lch-slider-label">H</label>
              <div className="card-color__lch-slider-track" style={{ background: hGradient }}>
                <input
                  type="range"
                  min={0}
                  max={360}
                  step={1}
                  value={h}
                  onChange={(e) => {
                    const newH = parseFloat(e.target.value);
                    applyStaticLch(l, clampedC, newH);
                  }}
                  className="card-color__lch-slider-input"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLinkedParameters = () => {
    // Get available reference colors (exclude current color)
    const availableRefColors = getAvailableReferenceColors(effectiveAvailableColors, currentColorId, isToken, colorSpace);
    
    // Use precomputed options to avoid recalculation in dropdown
    const referenceOptions = mapAvailableToOptions(availableRefColors);
    
    // Get reference color for placeholders - SINGLE SOURCE OF TRUTH: use precomputed colors only
    let refColorParsed = null;
    
    if (localReferenceColorId && precomputedColors && precomputedColors.get) {
      refColorParsed = precomputedColors.get(localReferenceColorId);
      
      // If no precomputed color and it's a static color, parse directly as fallback
      if (!refColorParsed) {
        const selectedRefColor = effectiveAvailableColors.find(c => c.id === localReferenceColorId);
        if (selectedRefColor && selectedRefColor.mode === 'static') {
          refColorParsed = parseColor(selectedRefColor.colorValue || selectedRefColor.hexValue, colorSpace);
        }
      }
    }
    
    const placeholders = getReferenceColorPlaceholders(refColorParsed, colorSpace);

    const handleReferenceColorChange = (option) => {
      setLocalReferenceColorId(option.value);
      // Trigger calculation immediately for Select since it doesn't support onBlur
      if (onReferenceColorChange) {
        onReferenceColorChange(option.value);
      }
    };



    return (
      <div className="card-color__parameters">
          <Select
            type="color"
            style="outline"
            value={localReferenceColorId}
            options={referenceOptions}
            placeholder="Select a color..."
            label="Reference color"
            onChange={handleReferenceColorChange}
            searchable={true}
            searchPlaceholder="Search colors..."
            className="card-color__reference-select"
            showSectionHeaders={true}
            primitivesSectionTitle={primitivesSectionTitle}
            themesSectionTitle={themesSectionTitle}
          />
        <div className="card-color__oklch-inputs">
          <LCHAInput
            component="l"
            value={localLExpression}
            referenceValue={refColorParsed?.oklch?.l || 0}
            colorSpace={colorSpace}
            onChange={(e) => handleLCHAChange('l', e.target.value)}
            onBlur={() => handleLCHABlur('l')}
            label="L"
            labelTooltip="Lightness"
            className="card-color__oklch-input"
          />
          <LCHAInput
            component="c"
            value={localCExpression}
            referenceValue={refColorParsed?.oklch?.c || 0}
            referenceOklch={refColorParsed?.oklch}
            colorSpace={colorSpace}
            placeholder={placeholders.c}
            onChange={(e) => handleLCHAChange('c', e.target.value)}
            onBlur={() => handleLCHABlur('c')}
            label="C"
            labelTooltip="Chroma, supports relative values: 0-100%"
            className="card-color__oklch-input"
          />
          <LCHAInput
            component="h"
            value={localHExpression}
            referenceValue={refColorParsed?.oklch?.h || 0}
            colorSpace={colorSpace}
            onChange={(e) => handleLCHAChange('h', e.target.value)}
            onBlur={() => handleLCHABlur('h')}
            label="H"
            labelTooltip="Hue"
            className="card-color__oklch-input"
          />
          <LCHAInput
            component="a"
            value={localAExpression}
            referenceValue={refColorParsed?.oklch?.alpha || 1}
            colorSpace={colorSpace}
            onChange={(e) => handleLCHAChange('a', e.target.value)}
            onBlur={() => handleLCHABlur('a')}
            label="A"
            labelTooltip="Alpha"
            className="card-color__oklch-input"
          />
        </div>
      </div>
    );
  };

  const renderContrastParameters = () => {
    // Get available reference colors (exclude current color)
    // Use precomputed options to avoid recalculation in dropdown
    const referenceOptions = mapAvailableToOptions(getAvailableReferenceColors(effectiveAvailableColors, currentColorId, isToken, colorSpace));
    
    const handleBackgroundColorChange = (option) => {
      setLocalBackgroundColorId(option.value);
      // Trigger calculation immediately for Select since it doesn't support onBlur
      if (onBackgroundColorChange) {
        onBackgroundColorChange(option.value);
      }
    };



    const handleForegroundColorChange = (option) => {
      setLocalForegroundColorId(option.value);
      // Trigger calculation immediately for Select since it doesn't support onBlur
      if (onForegroundColorChange) {
        onForegroundColorChange(option.value);
      }
    };



    return (
      <div className="card-color__parameters">
        <div className="bg-fg-selects-wrapper">
          <Select
            type="color"
            style="outline"
            value={localBackgroundColorId}
            options={referenceOptions}
            onChange={handleBackgroundColorChange}
            searchable={true}
            searchPlaceholder="Search colors..."
            label="Background color"
            className="card-color__reference-select"
            showSectionHeaders={true}
            primitivesSectionTitle={primitivesSectionTitle}
            themesSectionTitle={themesSectionTitle}
          />
          <Select
            type="color"
            style="outline"
            value={localForegroundColorId}
            options={referenceOptions}
            onChange={handleForegroundColorChange}
            searchable={true}
            searchPlaceholder="Search colors..."
            label="Foreground color"
            className="card-color__reference-select"
            showSectionHeaders={true}
            primitivesSectionTitle={primitivesSectionTitle}
            themesSectionTitle={themesSectionTitle}
          />
        </div>
        <div className="card-color__contrast-lc-row">
          <Input
            type="number"
            style="outline"
            value={localContrastLevel === 0 ? '' : localContrastLevel}
            onChange={handleContrastLevelChange}
            onKeyDown={handleContrastLevelKeyDown}
            onBlur={handleContrastLevelBlur}
            min={contrastAlgorithm === 'wcag' ? '1' : '0'}
            max={contrastAlgorithm === 'wcag' ? '21' : '108'}
            step={contrastAlgorithm === 'wcag' ? '0.1' : '1'}
            label={contrastAlgorithm === 'wcag' ? 'CR' : 'Lc'}
            labelTooltip={contrastAlgorithm === 'wcag' ? 'Contrast ratio' : 'Lightness contrast'}
            placeholder={contrastAlgorithm === 'wcag' ? '4.5' : undefined}
            fieldSuffix={contrastAlgorithm === 'wcag' ? ':1' : null}
            className={`card-color__contrast-input ${contrastAlgorithm === 'wcag' ? 'card-color__contrast-input--wcag' : ''}`}
          />
          <div className="card-color__alpha-wrapper">
            <label className="card-color__alpha-label tooltip" tabIndex={-1} data-tooltip="Alpha, adds transparency when possible">A</label>
            <div className="card-color__alpha-checkbox-container">
              <Checkbox
                checked={localContrastAlpha}
                onChange={(checked) => {
                  setLocalContrastAlpha(checked);
                  // Trigger calculation immediately for checkboxes
                  if (onContrastAlphaChange) {
                    onContrastAlphaChange(checked);
                  }
                }}
                className="card-color__alpha-checkbox"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSwatchColor = () => {
    // Check if calculation is impossible
    let isImpossible = false;
    if (mode === 'linked') {
      // For linked colors: missing reference color ID, color doesn't exist in effectiveAvailableColors, or invalid parsed color
      const referenceColorExists = localReferenceColorId && effectiveAvailableColors.some(c => c.id === localReferenceColorId);
      // Also check if parsedColor is the error fallback (gray with null oklch)
      const isErrorFallback = parsedColor && parsedColor.hex === '#808080' && !parsedColor.oklch;
      isImpossible = !localReferenceColorId || !referenceColorExists || !parsedColor || !parsedColor.isValid || isErrorFallback;
    } else if (mode === 'contrast') {
      // For contrast colors: missing IDs, colors don't exist in effectiveAvailableColors, or invalid parsed color
      const backgroundColorExists = localBackgroundColorId && effectiveAvailableColors.some(c => c.id === localBackgroundColorId);
      const foregroundColorExists = localForegroundColorId && effectiveAvailableColors.some(c => c.id === localForegroundColorId);
      // Also check if parsedColor is the error fallback (gray with null oklch)
      const isErrorFallback = parsedColor && parsedColor.hex === '#808080' && !parsedColor.oklch;
      isImpossible = !localBackgroundColorId || !localForegroundColorId || 
                     !backgroundColorExists || !foregroundColorExists || 
                     !parsedColor || !parsedColor.isValid || isErrorFallback;
    }
    
    let displayColor;
    
    if (mode === 'linked' || mode === 'contrast') {
      // For linked and contrast colors, always use the calculated color
      // Never fall back to parsing colorValue as it contains default values
      displayColor = parsedColor || { 
        hex: '#808080', // Gray loading state
        rgb: { r: 0.5, g: 0.5, b: 0.5, alpha: 1 },
        oklch: { l: 0.5, c: 0, h: 0, alpha: 1 },
        alpha: 1,
        isValid: true,
        isP3: false,
        contrastColor: '#FFFFFF',
        originalInput: 'loading'
      };
    } else {
      // For static colors, use parsed color or fallback to parsing colorValue
      displayColor = parsedColor || parseColor(colorValue, colorSpace) || { 
        hex: colorValue, 
        rgb: { r: 0.2, g: 0.7, b: 1, alpha: 1 },
        oklch: { l: 0.5, c: 0.25, h: 240, alpha: 1 },
        alpha: 1,
        isValid: true,
        isP3: p3Support,
        contrastColor: '#FFFFFF',
        originalInput: colorValue
      };
    }

    // Workspace gamut only — do not use displayColor.isP3 or stale P3 swatches persist in sRGB mode
    const showP3 = colorSpace === 'p3' && displayColor.isValid;
    const srgbHexFallback =
      displayColor.hex || (mode === 'static' ? localColorValue : colorValue);
    const p3HexBytes =
      displayColor.isValid && colorSpace === 'p3' ? formatP3Hex(displayColor) : null;
    const p3HexRowLabel =
      p3HexBytes && p3HexBytes.startsWith('#') ? `P3#${p3HexBytes.slice(1)}` : null;
    const showP3HexRow =
      colorSpace === 'p3' && displayColor.isValid && displayColor.isP3 && !!p3HexRowLabel;
    const oklchDisplayValue = displayColor.oklch ? formatOklch(displayColor.oklch) : (mode === 'static' ? localColorValue : oklchValue);
    const srgbHexLine = srgbHexFallback;

    const copySwatchLine = (e, text) => {
      e.preventDefault();
      e.stopPropagation();
      if (!text || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
      navigator.clipboard.writeText(text).catch(() => {});
    };

    const swatchLineKeyDown = (e, text) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      copySwatchLine(e, text);
    };
    // Determine current surface background from CSS variables and compose for contrast
    const computedStyles = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
    const bgColorCss = computedStyles ? computedStyles.getPropertyValue('--color-surface') : 'hsl(0, 0%, 0%)';
    // Parse simple hsl(...) values used in variables.css into sRGB 0..1
    const parseHslCss = (css) => {
      try {
        const m = css.match(/hsl\(\s*([0-9.]+)\s*,\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*\)/i);
        if (!m) return { r: 0, g: 0, b: 0 };
        const h = parseFloat(m[1]);
        const s = parseFloat(m[2]) / 100;
        const l = parseFloat(m[3]) / 100;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const hp = (h / 60) % 6;
        const x = c * (1 - Math.abs((hp % 2) - 1));
        let r1 = 0, g1 = 0, b1 = 0;
        if (0 <= hp && hp < 1) { r1 = c; g1 = x; b1 = 0; } else if (1 <= hp && hp < 2) { r1 = x; g1 = c; b1 = 0; } else if (2 <= hp && hp < 3) { r1 = 0; g1 = c; b1 = x; } else if (3 <= hp && hp < 4) { r1 = 0; g1 = x; b1 = c; } else if (4 <= hp && hp < 5) { r1 = x; g1 = 0; b1 = c; } else { r1 = c; g1 = 0; b1 = x; }
        const mVal = l - c / 2;
        return { r: r1 + mVal, g: g1 + mVal, b: b1 + mVal };
      } catch {
        return { r: 0, g: 0, b: 0 };
      }
    };
    const bgLinear = parseHslCss(bgColorCss);
    // Recompute text color locally so alpha and theme background are respected
    // For impossible state, use foreground-108 for consistent button color
    const textColor = isImpossible 
      ? 'var(--color-foreground-108)' 
      : getContrastColor(displayColor.rgb || { r: 0, g: 0, b: 0, alpha: 1 }, bgLinear);
    
    // Determine if color has transparency
    const alphaValue = displayColor.alpha !== undefined ? displayColor.alpha : 
                      (displayColor.rgb && displayColor.rgb.alpha !== undefined ? displayColor.rgb.alpha : 1);
    const hasAlpha = alphaValue < 1;
    
    // Generate proper CSS color with P3 support
    const cssColor = generateP3CSS(displayColor, colorSpace);
    
    // For browsers that don't support `color(display-p3 …)`, fall back to
    // **sRGB** hex/rgba — never use Display-P3 hex bytes here, or CSS would
    // misinterpret them as sRGB and shift the swatch hue.
    const fallbackColor = hasAlpha ? 
      `rgba(${Math.round(displayColor.rgb.r * 255)}, ${Math.round(displayColor.rgb.g * 255)}, ${Math.round(displayColor.rgb.b * 255)}, ${alphaValue})` :
      srgbHexFallback;

    return (
      <div className={`card-color__swatch ${showP3 ? 'card-color__swatch--p3' : ''} ${isImpossible ? 'card-color__swatch--impossible' : ''}`}>
        <div 
          className="card-color__color-preview"
          style={isImpossible ? {
            backgroundColor: 'var(--color-surface)'
          } : {
            backgroundColor: fallbackColor,
            // Use CSS custom property for P3 support
            '--color-display': cssColor
          }}
        >
          {isImpossible && (
            <svg 
              className="card-color__pattern-overlay"
              width="100%" 
              height="100%" 
              style={{ 
                position: 'absolute', 
                inset: 0, 
                pointerEvents: 'none',
                color: 'var(--color-foreground-0)'
              }}
            >
              <defs>
                <pattern id={`dashed-pattern-${currentColorId || 'default'}`} x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
                  <path d="M16 0.707031L0.707031 16H0V15.293L15.293 0H16V0.707031Z" fill="currentColor" />
                  <path d="M16 16H15.293L16 15.293V16Z" fill="currentColor" />
                  <path d="M0 0.707031V0H0.707031L0 0.707031Z" fill="currentColor" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill={`url(#dashed-pattern-${currentColorId || 'default'})`} />
            </svg>
          )}
          {!isDemo && (
            <div className="card-color__more-button-wrapper" ref={buttonRef}>
              <Button
                type="ghost"
                size="medium"
                icon={<Icon name="more-vert" size="medium" style={{ color: textColor }} />}
                onClick={handleMoreAction}
                className="card-color__more-button tooltip"
                data-tooltip="More"
                data-tooltip-position="bottom"
                style={{ color: textColor }}
              />
            </div>
          )}
          <div className="card-color__color-info">
            {!isImpossible && (
              <div className="card-color__color-values" style={{ color: textColor }}>
                {showP3HexRow && (
                  <div
                    className="card-color__oklch-value card-color__swatch-value-row tooltip tooltip--max-160"
                    style={{ color: textColor }}
                    role="button"
                    tabIndex={0}
                    data-tooltip="Display-P3 channel hex (8-bit). Wider than sRGB; click to copy."
                    data-tooltip-position="top"
                    onClick={(e) => copySwatchLine(e, p3HexBytes)}
                    onKeyDown={(e) => swatchLineKeyDown(e, p3HexBytes)}
                  >
                    {p3HexRowLabel}
                  </div>
                )}
                <div
                  className="card-color__oklch-value card-color__swatch-value-row tooltip tooltip--max-160"
                  style={{ color: textColor }}
                  role="button"
                  tabIndex={0}
                  data-tooltip="OKLCH in workspace gamut. Click to copy."
                  data-tooltip-position="top"
                  onClick={(e) => copySwatchLine(e, oklchDisplayValue)}
                  onKeyDown={(e) => swatchLineKeyDown(e, oklchDisplayValue)}
                >
                  {oklchDisplayValue}
                </div>
                <div
                  className="card-color__hex-value card-color__swatch-value-row tooltip tooltip--max-160"
                  style={{ color: textColor }}
                  role="button"
                  tabIndex={0}
                  data-tooltip="sRGB hex (8-bit, sRGB gamut). Click to copy."
                  data-tooltip-position="top"
                  onClick={(e) => copySwatchLine(e, srgbHexLine)}
                  onKeyDown={(e) => swatchLineKeyDown(e, srgbHexLine)}
                >
                  {srgbHexLine}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={combinedClassName} {...props}>
        <div className={contentClassName}>
          {renderTypeAndName()}
          {mode === 'static' && renderStaticParameters()}
          {mode === 'linked' && renderLinkedParameters()}
          {mode === 'contrast' && renderContrastParameters()}
        </div>
        {renderSwatchColor()}
      </div>
      {!isDemo && renderDropdown()}
      {!isDemo && (
        <MoveModal
          isOpen={showMoveModal}
          onClose={handleMoveModalClose}
          onMove={handleMoveModalMove}
          folders={folderOptions}
          currentFolderId={currentFolderId}
          itemType="color"
          itemName={colorName}
        />
      )}
    </>
  );
};
